import React, { useState, useEffect, useRef } from 'react';
import {
    XCircle,
    ChevronDown,
    ChevronUp,
    RefreshCw,
} from 'lucide-react';


const MultiSelectDropdown = ({ label, id, options, selectedValues, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const isSelected = (value) => selectedValues && selectedValues.includes(value);

    const toggleOption = (option) => {
        if (isSelected(option)) {
            onChange(selectedValues.filter(item => item !== option));
        } else {
            onChange([...selectedValues, option]);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <label
                htmlFor={id}
                className="block text-xs font-medium mb-1.5"
                style={{ color: '#999999' }}
            >
                {label}
            </label>
            <div
                className="rounded-[14px] px-4 py-2.5 cursor-pointer flex items-center justify-between transition-all duration-300"
                style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    border: isOpen
                        ? '1px solid rgba(232,184,0,0.5)'
                        : '1px solid rgba(255,255,255,0.1)',
                    boxShadow: isOpen
                        ? '0 4px 20px rgba(232,184,0,0.15)'
                        : '0 2px 8px rgba(0,0,0,0.3)',
                }}
                onClick={() => setIsOpen(!isOpen)}
            >
                <span
                    className="text-sm truncate"
                    style={{ color: '#F5F5F5' }}
                >
                    {selectedValues.length === 0
                        ? (label === 'Class' ? `All ${label}es` : `All ${label}s`)
                        : selectedValues.length === 1
                            ? selectedValues[0]
                            : `${selectedValues.length} selected`}
                </span>
                <span style={{ color: '#999999' }}>
                    {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </span>
            </div>

            {isOpen && (
                <div
                    className="absolute z-10 mt-2 w-full max-h-60 rounded-[14px] overflow-hidden"
                    style={{
                        background: 'linear-gradient(135deg, rgba(30,30,30,0.98) 0%, rgba(20,20,20,0.98) 100%)',
                        backdropFilter: 'blur(20px)',
                        WebkitBackdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                    }}
                >
                    {/* ALL Option */}
                    <div
                        className="p-2"
                        style={{
                            borderBottom: '1px solid rgba(255,255,255,0.06)'
                        }}
                    >
                        <label
                            className="flex items-center p-2 rounded-lg cursor-pointer transition-all duration-200"
                            style={{
                                background: selectedValues.length === 0
                                    ? 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)'
                                    : 'transparent',
                            }}
                            onMouseEnter={(e) => {
                                if (selectedValues.length !== 0) {
                                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (selectedValues.length !== 0) {
                                    e.currentTarget.style.background = 'transparent';
                                }
                            }}
                        >
                            <input
                                type="checkbox"
                                className="h-4 w-4 rounded"
                                style={{ accentColor: '#E8B800' }}
                                checked={selectedValues.length === 0}
                                onChange={() => onChange([])}
                            />
                            <span
                                className="ml-2 text-sm font-medium"
                                style={{ color: '#F5F5F5' }}
                            >
                                All {label}s
                            </span>
                        </label>
                    </div>

                    {/* Individual options */}
                    <div className="max-h-48 overflow-y-auto p-1">
                        {options.map(option => (
                            <label
                                key={option}
                                className="flex items-center p-2 rounded-lg cursor-pointer transition-all duration-200"
                                style={{
                                    background: isSelected(option)
                                        ? 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)'
                                        : 'transparent',
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected(option)) {
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected(option)) {
                                        e.currentTarget.style.background = 'transparent';
                                    }
                                }}
                            >
                                <input
                                    type="checkbox"
                                    className="h-4 w-4 rounded"
                                    style={{ accentColor: '#E8B800' }}
                                    checked={isSelected(option)}
                                    onChange={() => toggleOption(option)}
                                />
                                <span
                                    className="ml-2 text-sm"
                                    style={{ color: '#F5F5F5' }}
                                >
                                    {option}
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};


const SliderFilter = ({ label, id, min, max, value, onChange }) => {
    const handleMinChange = (e) => {
        const newMin = parseInt(e.target.value);
        if (!isNaN(newMin) && newMin >= min && newMin <= value[1]) {
            onChange([newMin, value[1]]);
        }
    };

    const handleMaxChange = (e) => {
        const newMax = parseInt(e.target.value);
        if (!isNaN(newMax) && newMax <= max && newMax >= value[0]) {
            onChange([value[0], newMax]);
        }
    };

    return (
        <div className="w-full">
            <div className="flex items-center mb-2">
                <label
                    htmlFor={id}
                    className="block text-xs font-medium"
                    style={{ color: '#999999' }}
                >
                    {label}
                </label>
                <div className="flex items-center ml-auto">
                    <input
                        id={`${id}-min`}
                        type="number"
                        min={min}
                        max={value[1]}
                        value={value[0]}
                        onChange={handleMinChange}
                        className="w-14 h-7 px-2 text-xs rounded-lg focus:outline-none"
                        style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#F5F5F5',
                        }}
                        aria-label="Minimum SLAM score"
                    />
                    <span className="text-xs mx-2" style={{ color: '#555555' }}>to</span>
                    <input
                        id={`${id}-max`}
                        type="number"
                        min={value[0]}
                        max={max}
                        value={value[1]}
                        onChange={handleMaxChange}
                        className="w-14 h-7 px-2 text-xs rounded-lg focus:outline-none"
                        style={{
                            background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
                            border: '1px solid rgba(255,255,255,0.1)',
                            color: '#F5F5F5',
                        }}
                        aria-label="Maximum SLAM score"
                    />
                </div>
            </div>
            <div className="relative mt-3">
                <div
                    className="h-2 rounded-full"
                    style={{
                        background: 'rgba(255,255,255,0.1)',
                    }}
                >
                    <div
                        className="absolute h-2 rounded-full"
                        style={{
                            left: `${((value[0] - min) / (max - min)) * 100}%`,
                            right: `${100 - ((value[1] - min) / (max - min)) * 100}%`,
                            background: 'linear-gradient(135deg, #E8B800 0%, #FFA500 100%)',
                            boxShadow: '0 0 8px rgba(232,184,0,0.5)',
                        }}
                    />
                </div>
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={value[0]}
                    onChange={(e) => onChange([parseInt(e.target.value), value[1]])}
                    className="absolute top-0 h-2 w-full opacity-0 cursor-pointer"
                />
                <input
                    type="range"
                    min={min}
                    max={max}
                    value={value[1]}
                    onChange={(e) => onChange([value[0], parseInt(e.target.value)])}
                    className="absolute top-0 h-2 w-full opacity-0 cursor-pointer"
                />
            </div>
        </div>
    );
};

const TransferPortalFilters = ({
    positions,
    classes,
    selectedSchema,
    latestTeams,
    destinations,
    filters,
    handleFilterChange,
    removeFilter,
    // searchTerm,
    // setSearchTerm,
    resetFilters
}) => {
    return (
        <div
            className="px-6 py-6 rounded-[14px] mb-4"
            style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
        >
            <div className="flex items-center justify-between mb-5">
                <h3
                    className="text-lg font-semibold"
                    style={{ color: '#F5F5F5' }}
                >
                    Filter Players
                </h3>
                <button
                    className="flex items-center px-4 py-2 text-sm font-medium rounded-[14px] transition-all duration-300"
                    style={{
                        background: 'linear-gradient(135deg, rgba(232,184,0,0.1) 0%, rgba(255,165,0,0.05) 100%)',
                        border: '1px solid rgba(232,184,0,0.2)',
                        color: '#E8B800',
                    }}
                    onClick={resetFilters}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #E8B800 0%, #FFA500 100%)';
                        e.currentTarget.style.color = '#000';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(232,184,0,0.3)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(232,184,0,0.1) 0%, rgba(255,165,0,0.05) 100%)';
                        e.currentTarget.style.color = '#E8B800';
                        e.currentTarget.style.boxShadow = 'none';
                    }}
                >
                    <RefreshCw size={14} className="mr-1.5" />
                    Reset
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
                {/* <div className="w-full">
                    <label htmlFor="search" className="block text-xs font-medium text-[#999999] mb-1">Search</label>
                    <div className="relative">
                        <input
                            type="text"
                            id="search"
                            style={{ height: '38.64px' }}
                            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#1A1A1A] shadow-sm focus:border-[#E8B800] focus:ring focus:ring-[#E8B800] focus:ring-opacity-50 transition-colors"
                            placeholder="Player or team name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-[#999999]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#999999] hover:text-[#F5F5F5]"
                            >
                                <XCircle size={16} />
                            </button>
                        )}
                    </div>
                </div> */}

                <div>
                    <MultiSelectDropdown
                        label="Position"
                        id="position-filter"
                        options={positions}
                        selectedValues={filters.position}
                        onChange={(value) => handleFilterChange('position', value)}
                    />
                </div>

                <div>
                    <MultiSelectDropdown
                        label="Class"
                        id="class-filter"
                        options={classes}
                        selectedValues={filters.class}
                        onChange={(value) => handleFilterChange('class', value)}
                    />
                </div>

                <div>
                    <MultiSelectDropdown
                        label="Team"
                        id="team-filter"
                        options={latestTeams}
                        selectedValues={filters.latestTeam}
                        onChange={(value) => handleFilterChange('latestTeam', value)}
                    />
                </div>
                {selectedSchema == 'MBB' ? <div className="w-full">
                    <label htmlFor="min-possessions" className="block text-xs font-medium text-[#999999] mb-1">Min Possessions</label>
                    <input
                        type="number"
                        id="min-possessions"
                        style={{ height: '38.64px' }}
                        className="w-full px-3 py-2 rounded-lg border border-[#1A1A1A] shadow-sm focus:border-[#E8B800] focus:ring focus:ring-[#E8B800] focus:ring-opacity-50 transition-colors"
                        placeholder="Min possessions..."
                        value={filters.min_possessions || ''}
                        onChange={(e) => handleFilterChange('min_possessions', e.target.value === '' ? null : parseInt(e.target.value))}
                    />
                </div> : <div>
                    <MultiSelectDropdown
                        label="Destination"
                        id="destination-filter"
                        options={destinations}
                        selectedValues={filters.destination}
                        onChange={(value) => handleFilterChange('destination', value)}
                    />
                </div>}

                {selectedSchema != 'MBB' && <div>
                    <SliderFilter
                        label="SLAM Score"
                        id="slam-score-filter"
                        min={0}
                        max={100}
                        value={filters.slamScore}
                        onChange={(value) => handleFilterChange('slamScore', value)}
                    />
                </div>}
            </div>

            {/* Filter Pills */}
            {(filters.position.length > 0 || filters.class.length > 0 || filters.latestTeam.length > 0 || filters.destination.length > 0 ||
                filters.slamScore[0] > 0 || filters.slamScore[1] < 100 || filters.min_possessions) && (
                    <div className="mt-6 flex flex-wrap gap-2 items-center">
                        <span
                            className="text-xs font-medium mr-2"
                            style={{ color: '#555555' }}
                        >
                            Active Filters:
                        </span>

                        {filters.position.map(pos => (
                            <span
                                key={pos}
                                className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-sm font-medium transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)',
                                    border: '1px solid rgba(232,184,0,0.3)',
                                    color: '#E8B800',
                                }}
                            >
                                {pos}
                                <button
                                    className="ml-2 transition-colors hover:text-red-500"
                                    onClick={() => removeFilter('position', pos)}
                                    aria-label={`Remove ${pos} filter`}
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        ))}

                        {filters.class.map(cls => (
                            <span
                                key={cls}
                                className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-sm font-medium transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)',
                                    border: '1px solid rgba(232,184,0,0.3)',
                                    color: '#E8B800',
                                }}
                            >
                                {cls}
                                <button
                                    className="ml-2 transition-colors hover:text-red-500"
                                    onClick={() => removeFilter('class', cls)}
                                    aria-label={`Remove ${cls} filter`}
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        ))}

                        {filters.latestTeam.map(team => (
                            <span
                                key={team}
                                className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-sm font-medium transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)',
                                    border: '1px solid rgba(232,184,0,0.3)',
                                    color: '#E8B800',
                                }}
                            >
                                {team}
                                <button
                                    className="ml-2 transition-colors hover:text-red-500"
                                    onClick={() => removeFilter('latestTeam', team)}
                                    aria-label={`Remove ${team} filter`}
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        ))}

                        {filters.destination.map(dest => (
                            <span
                                key={dest}
                                className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-sm font-medium transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)',
                                    border: '1px solid rgba(232,184,0,0.3)',
                                    color: '#E8B800',
                                }}
                            >
                                {dest}
                                <button
                                    className="ml-2 transition-colors hover:text-red-500"
                                    onClick={() => removeFilter('destination', dest)}
                                    aria-label={`Remove ${dest} filter`}
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        ))}

                        {/* SLAM Score Filter Pill */}
                        {(filters.slamScore[0] > 0 || filters.slamScore[1] < 100) && (
                            <span
                                className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-sm font-medium transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)',
                                    border: '1px solid rgba(232,184,0,0.3)',
                                    color: '#E8B800',
                                }}
                            >
                                SLAM Score: {filters.slamScore[0]}-{filters.slamScore[1]}
                                <button
                                    className="ml-2 transition-colors hover:text-red-500"
                                    onClick={() => handleFilterChange('slamScore', [0, 100])}
                                    aria-label="Reset SLAM score filter"
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        )}

                        {/* Min Possessions Filter Pill */}
                        {filters.min_possessions && (
                            <span
                                className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-sm font-medium transition-all duration-200"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(232,184,0,0.15) 0%, rgba(255,165,0,0.1) 100%)',
                                    border: '1px solid rgba(232,184,0,0.3)',
                                    color: '#E8B800',
                                }}
                            >
                                Min Possessions: {filters.min_possessions}
                                <button
                                    className="ml-2 transition-colors hover:text-red-500"
                                    onClick={() => handleFilterChange('min_possessions', null)}
                                    aria-label="Reset min possessions filter"
                                >
                                    <XCircle size={14} />
                                </button>
                            </span>
                        )}

                        {(filters.position.length > 0 || filters.class.length > 0 || filters.latestTeam.length > 0 ||
                            filters.destination.length > 0 || filters.slamScore[0] > 0 || filters.slamScore[1] < 100 || filters.min_possessions) && (
                                <button
                                    className="inline-flex items-center px-3 py-1.5 rounded-[14px] text-xs font-medium transition-all duration-300"
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        color: '#999999',
                                    }}
                                    onClick={resetFilters}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = 'rgba(255,100,100,0.1)';
                                        e.currentTarget.style.borderColor = 'rgba(255,100,100,0.3)';
                                        e.currentTarget.style.color = '#ff6b6b';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.03) 100%)';
                                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                                        e.currentTarget.style.color = '#999999';
                                    }}
                                >
                                    Clear All
                                </button>
                            )}
                    </div>
                )}
        </div>
    );
};
export default TransferPortalFilters;